import { ApiClient, type ChangeSignal, SupabaseBroadcastSignal } from '@rp/client-core';
import type { MeView, PairDeviceResult } from '@rp/contracts';
import { createClient, type Session } from '@supabase/supabase-js';

/**
 * Infrastructure for the browser: Supabase is used ONLY for sign-in sessions and
 * realtime change signals. All reads and writes go through the platform API.
 * Only public values (URL, anon key) exist in this bundle.
 */
/**
 * True when this page was served by the in-store MY FOOD Hub (it adds <meta name="myfood-hub">).
 * The same build then talks to the hub for everything: sign-in, API and updates, so it keeps
 * working on the restaurant network without internet (docs/OFFLINE-ARCHITECTURE.md).
 */
export const onHub =
  typeof document !== 'undefined' && document.querySelector('meta[name="myfood-hub"]') !== null;
const SUPABASE_URL = onHub ? window.location.origin : (import.meta.env.VITE_SUPABASE_URL as string);
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
const API_URL = onHub ? '' : ((import.meta.env.VITE_API_URL as string | undefined) ?? '');
/** On the hub, screens refresh from the hub this often (local network, no cloud realtime). */
const HUB_REFRESH_MS = 2000;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: { persistSession: true, autoRefreshToken: true, storageKey: onHub ? 'rp.hub.session' : 'rp.session' },
});

// Private realtime channels need the current access token (cloud only; the hub has no realtime).
supabase.auth.onAuthStateChange((_event, session) => {
  if (session && !onHub) void supabase.realtime.setAuth(session.access_token);
});

const POS_DEVICE_KEY = onHub ? 'rp.hub.posDevice' : 'rp.posDevice';

interface TillIdentity {
  id: string;
  name: string;
  /** The till's own device login (rotating refresh token): unlocks staff sessions by PIN. */
  refreshToken?: string;
}

/** A paired POS till: identifies the terminal (x-device-id) while staff sign in on it. */
export function posDevice(): { id: string; name: string; pinReady: boolean } | null {
  const t = readTill();
  return t ? { id: t.id, name: t.name, pinReady: Boolean(t.refreshToken) } : null;
}
function readTill(): TillIdentity | null {
  try {
    const raw = localStorage.getItem(POS_DEVICE_KEY);
    return raw ? (JSON.parse(raw) as TillIdentity) : null;
  } catch {
    return null;
  }
}

export async function currentSession(): Promise<Session | null> {
  return (await supabase.auth.getSession()).data.session;
}

export async function signInStaff(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error)
    throw new Error(
      error.message === 'Invalid login credentials' ? 'Wrong email or password' : error.message,
    );
}

/** Emails a link to /set-password. Same answer whether or not the address has an account. */
export async function requestPasswordReset(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/set-password`,
  });
  if (error && error.status !== 400 && error.status !== 404) throw new Error(error.message);
}

/** Sets the password for the account signed in through a reset / invitation link. */
export async function setNewPassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password });
  if (!error) return;
  if (error.code === 'weak_password')
    throw new Error(
      'Choose a stronger password: at least 10 characters, and not one known from data breaches.',
    );
  if (error.code === 'same_password') throw new Error('Choose a password you have not used here before.');
  throw new Error(error.message);
}

export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

export const api = new ApiClient({
  baseUrl: API_URL,
  getAccessToken: async () => {
    const session = await currentSession();
    if (!session) throw new Error('Not signed in');
    return session.access_token;
  },
  getDeviceId: () => posDevice()?.id ?? null,
});

/** Owner onboarding, step 1 (public): emails a verification link if this address was invited. */
export async function startOwnerOnboarding(email: string): Promise<void> {
  const res = await fetch(`${API_URL}/v1/onboarding/start`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok)
    throw new Error((await res.json().catch(() => null))?.error?.message ?? 'Something went wrong');
}

/** Owner onboarding, step 2: with the session from the email link, become the restaurant's Owner. */
export async function acceptOwnerInvitation(
  fullName: string,
  pin: string,
): Promise<{ restaurantName: string }> {
  const session = await currentSession();
  if (!session) throw new Error('Open the link from your email to continue');
  const res = await fetch(`${API_URL}/v1/onboarding/accept`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${session.access_token}` },
    body: JSON.stringify({ fullName, pin }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error?.message ?? 'Something went wrong');
  // This browser is now the owner's registered device: keep its login, then sign in with the PIN.
  await adoptPairing(body.device as PairDeviceResult);
  await signInWithPin(pin);
  return { restaurantName: body.restaurantName };
}

/**
 * Owner / manager on a new phone or laptop: email + PIN. The server registers this browser as their
 * own device; then the PIN signs in as usual, and next time only the PIN is asked.
 */
export async function signInWithEmailAndPin(email: string, pin: string): Promise<void> {
  const res = await fetch(`${API_URL}/v1/auth/email-pin`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, pin }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error?.message ?? 'Sign-in failed. Try again.');
  await adoptPairing(body as PairDeviceResult);
  await signInWithPin(pin);
}

/** "Email me a sign-in link" (owners and managers). Same answer for every address. */
export async function sendSignInLink(email: string): Promise<void> {
  const res = await fetch(`${API_URL}/v1/auth/email-link`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok)
    throw new Error((await res.json().catch(() => null))?.error?.message ?? 'Something went wrong');
}

/**
 * With the session from an emailed sign-in link: registers this browser as the person's own device
 * for PIN sign-in, then ends the email session. The PIN pad is next.
 */
export async function registerThisDevice(): Promise<PairDeviceResult> {
  return adoptPairing(await api.registerPersonalDevice());
}

/** Exchanges a one-time pairing code for this device's own login (no shared passwords). */
export async function pairDevice(code: string): Promise<PairDeviceResult> {
  const res = await fetch(`${API_URL}/v1/devices/pair`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  const body = await res.json();
  if (!res.ok) throw new Error(body?.error?.message ?? 'Pairing failed');
  return adoptPairing(body as PairDeviceResult);
}

/** Device-initiated pairing: a code to show on this screen, and a secret kept only in memory. */
export async function requestPairing(
  replaces: string | null = null,
): Promise<{ code: string; secret: string; expiresAt: string }> {
  const res = await fetch(`${API_URL}/v1/devices/pairing-requests`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(replaces ? { replaces } : {}),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new Error(body?.error?.message ?? 'Could not get a pairing code. Check the connection.');
  return body;
}

/** Asks whether a manager has approved this device's code; pairs the device when they have. */
export async function collectPairing(secret: string): Promise<PairDeviceResult | null> {
  const res = await fetch(`${API_URL}/v1/devices/pairing-requests/collect`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ secret }),
  });
  const body = await res.json();
  if (!res.ok) {
    const error = new Error(body?.error?.message ?? 'Pairing failed') as Error & { code?: string };
    error.code = body?.error?.code;
    throw error;
  }
  return body.status === 'paired' ? adoptPairing(body as PairDeviceResult) : null;
}

async function adoptPairing(result: PairDeviceResult): Promise<PairDeviceResult> {
  if (result.device.kind === 'pos') {
    // A till keeps its own device login (kept apart from staff sessions) so staff can unlock it with
    // their PIN; staff then act as themselves on this terminal.
    const till: TillIdentity = {
      id: result.device.id,
      name: result.device.name,
      refreshToken: result.session.refreshToken,
    };
    localStorage.setItem(POS_DEVICE_KEY, JSON.stringify(till));
    await supabase.auth.signOut();
  } else {
    await supabase.auth.setSession({
      access_token: result.session.accessToken,
      refresh_token: result.session.refreshToken,
    });
  }
  return result;
}

// ---------------------------------------------------------------------------
// Till login + staff PINs
// ---------------------------------------------------------------------------
let tillAccess: { token: string; expiresAt: number } | null = null;
let tillRefresh: Promise<string> | null = null;

/** Access token of the till's own device login (refresh token rotates; one refresh at a time). */
async function tillToken(): Promise<string> {
  if (tillAccess && tillAccess.expiresAt - 60 > Date.now() / 1000) return tillAccess.token;
  tillRefresh ??= (async () => {
    const till = readTill();
    if (!till?.refreshToken) throw new Error('This till is not set up for PIN sign-in. Pair it again.');
    const res = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`, {
      method: 'POST',
      headers: { apikey: SUPABASE_ANON_KEY, 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: till.refreshToken }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      access_token?: string;
      refresh_token?: string;
      expires_at?: number;
    };
    if (!res.ok || !body.access_token || !body.refresh_token) {
      if (res.status === 400 || res.status === 401)
        throw new Error('This till was removed or its login expired. Ask a manager to pair it again.');
      throw new Error('No connection to the server. Check the network and try again.');
    }
    localStorage.setItem(POS_DEVICE_KEY, JSON.stringify({ ...till, refreshToken: body.refresh_token }));
    tillAccess = { token: body.access_token, expiresAt: body.expires_at ?? Date.now() / 1000 + 3000 };
    return body.access_token;
  })().finally(() => {
    tillRefresh = null;
  });
  return tillRefresh;
}

/** API calls made by the till itself (PIN sign-in, PIN recovery), not by a staff member. */
export const tillApi = new ApiClient({ baseUrl: API_URL, getAccessToken: tillToken, deviceId: null });

/** Unlocks a staff session on this till with the staff member's PIN. */
export async function signInWithPin(pin: string): Promise<{ displayName: string; mustChangePin: boolean }> {
  const r = await tillApi.pinSignIn(pin);
  const { error } = await supabase.auth.setSession({
    access_token: r.session.accessToken,
    refresh_token: r.session.refreshToken,
  });
  if (error) throw new Error('Sign-in could not be completed. Try again.');
  return { displayName: r.displayName, mustChangePin: r.mustChangePin };
}

export function branchSignal(topic: string): ChangeSignal {
  if (onHub) {
    // The hub is on the local network: refreshing every couple of seconds is cheap and needs no internet.
    return {
      subscribe: (onChange, onStatus) => {
        onStatus('connected');
        const timer = window.setInterval(onChange, HUB_REFRESH_MS);
        return () => window.clearInterval(timer);
      },
    };
  }
  return new SupabaseBroadcastSignal(supabase, topic);
}

export const topics = {
  orders: (branchId: string) => `branch:${branchId}:orders`,
  station: (branchId: string, stationId: string) => `branch:${branchId}:station:${stationId}`,
  ops: (branchId: string) => `branch:${branchId}:ops`,
};

export function hasPermission(me: MeView | null, permission: string, branchId?: string): boolean {
  if (!me) return false;
  return Object.entries(me.permissions).some(
    ([branch, perms]) => (branch === '*' || !branchId || branch === branchId) && perms.includes(permission),
  );
}
